import {
  Avatar,
  Badge,
  ButtonList,
  CollapseCard,
  CopyToClipboardButton,
  FlexBox,
  IconButton,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { useHasAccess } from 'lib-shared-access-control';
import { convertChannelCasing } from 'lib-shared-api-utils';
import { ConversationsChannels } from 'lib-shared-legacy-types';
import { ampli } from 'lib-shared-tracking';
import { User } from 'lib-shared-types-api';
import { Channel } from 'lib-shared-types-channels';
import { MouseEvent, useCallback, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { useMap } from 'react-use';
import { MarkRequired } from 'ts-essentials';

import { appActions } from 'app/app.actions';
import { RootReducerStateT } from 'app/reducers/rootReducer';
import { validateIfUserCanMerge } from 'app/views/layout/utils/validateIfUserCanMerge/validateIfUserCanMerge';
import { AppDispatch } from 'bootstrap.store';
import { CustomFieldChip } from 'components/customFields';
import { BanUserButtonListItem } from 'components/shared/banUserButtonListItem/banUserButtonListItem';
import { ToggleUserCsatExclusionButtonListItem } from 'components/shared/toggleUserCsatExclusionButtonListItem';
import { UnbanUserButtonListItem } from 'components/shared/unbanUserButtonListItem/unbanUserButtonListItem';
import { userModalActions } from 'components/usersModal';
import { bansApi } from 'hooks/useBans/api';
import { UserBan } from 'hooks/useBans/types';
import { channelAttributes } from 'utils/channels';
import { useLazyCustomFields } from 'utils/customFields';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { bindActionToPromise } from 'utils/sagaApiGeneratorHelpers';

import { twilioActions } from '../../../../../app/middleware/twilio/actions/twilioActions';
import { numberFormatterInternational } from '../../../../../app/utils/telephonyHelpers';
import { composeEmailActions } from '../../../../shared/composeEmail/actions/composeEmailActions';

import { ContactCardLine } from './contactCardLine';

import '../../../styles/cards.less';

const { getUserBans } = bansApi;

type Props = {
  /** Whether or not the sidebar is minimized. */
  isMinimized?: boolean;
  /** Whether or not the contact details dropdown is open. */
  isOpen: boolean;
  /** Invoked whenever the contact details dropdown opened state is toggled. */
  onToggle: () => void;
} & StateProps &
  DispatchProps;

/**
 * List of available channels that the user is allowed to be banned on through
 * the UI.
 */
const ALLOWED_BANNING_CHANNELS: Channel[] = ['widget_chat', 'messenger'];

/**
 * Whether or not the ban item should be shown for the given channel. Cast to
 * ConversationChannel in order to reuse the enum which defines same channels.
 */
const shouldShowBanItem = (channel?: ConversationsChannels | null) =>
  channel &&
  ALLOWED_BANNING_CHANNELS.includes(
    convertChannelCasing(channel, 'snake') as Channel,
  );

const ContactCard = (props: Props): JSX.Element | null => {
  const hasBanAccess = useHasAccess('contact.ban');
  const hasCustomAttributeAccess = useHasAccess('contact.custom_attribute');
  const hasMergeAccess = useHasAccess('contact.merge');
  const hasReplyAccess = useHasAccess('conversation.reply');
  const flags = useFeatureFlag(['agnostic-csat-settings']);

  const [bans, { setAll: setAllBans }] = useMap<
    Map<Channel, UserBan | undefined>
  >(new Map([]));

  const getActiveUserBans = useCallback(async () => {
    const userId = props.user?.id;
    if (!userId) return;

    const {
      data: { bans },
    } = await getUserBans({
      params: {
        end_user_id: userId,
        active_state: 'active',
      },
    });

    const allowedChannelBans = new Map(
      ALLOWED_BANNING_CHANNELS.map((channel) => [
        channel,
        bans.find((ban) => ban.channel === channel),
      ]),
    );

    setAllBans(allowedChannelBans);
  }, [setAllBans, props?.user?.id]);

  useEffect(() => {
    getActiveUserBans();
  }, [getActiveUserBans]);

  const [readCustomFields, { customFields: rawCustomFields }] =
    useLazyCustomFields();

  useEffect(() => {
    if (props.user?.id) {
      readCustomFields({
        entityId: 'contact',
        entityInstanceId: props.user.id,
      });
    }
  }, [props.user?.id, readCustomFields]);

  const customFields = useMemo(
    () =>
      rawCustomFields.filter(
        (customField) => customField.value !== undefined,
      ) as readonly MarkRequired<(typeof rawCustomFields)[number], 'value'>[],
    [rawCustomFields],
  );

  const handleMakeCall = (
    e: MouseEvent<HTMLAnchorElement>,
    phoneNumber?: string,
  ): void => {
    // Prevent default so user phone client is not opened.
    e.preventDefault();
    const { telephonyMakeCall, user, telephonyFromNumber } = props;
    const toNumber = phoneNumber ?? user?.phone_number;
    if (!user || !toNumber) {
      return;
    }

    telephonyMakeCall({
      userId: user.id,
      toNumber: toNumber,
      fromNumber: telephonyFromNumber?.e164_format as string,
    });

    ampli.conversationSidebarCustomerInfoPhoneNumberClicked();
  };

  const handleEmailOnClick = (
    e: MouseEvent<HTMLAnchorElement>,
    email?: string,
  ): void => {
    // Prevent default so user email client is not opened.
    e.preventDefault();
    const { user } = props;

    if (!user) return;

    props.showComposeEmailModal({
      initialValues: {
        toUser: [{ user: { ...user, ...(!!email && { email }) } }],
      },
    });

    ampli.conversationSidebarCustomerInfoEmailClicked();
  };

  /** Gets formatted phone number or null in case the user doesn't have a phone. */
  const getPhoneNumberFormat = (userPhoneNumber: string | null): string => {
    const phoneNumber = userPhoneNumber?.trim();

    if (!phoneNumber) return '';

    return phoneNumber.startsWith('+')
      ? numberFormatterInternational({ phoneNumber })
      : phoneNumber;
  };

  /** Gets the avatar element to use. */
  const getAvatarElement = () => {
    const { user = null } = props;
    return <Avatar className="header-icon" user={user} />;
  };

  /**
   * Because the backend is handling ban in two different lists the backend two
   * endpoint for ban is not 100% sync, therefor we need to delay the get
   * callback.
   */

  const getActiveUserBansDelayed = () => {
    setTimeout(() => {
      getActiveUserBans();
    }, 2000);
  };

  /** Gets the menu element to use in the dropdown. */
  const getMenuElement = (user: User) => {
    const {
      showEditUserModal,
      conversation,
      showAddContactAttributesModal,
      showMergeContactsModal,
    } = props;
    const { initial_channel, id, attributes } = conversation;
    const csid = id ? id : undefined;
    const ipAddress = attributes?.ip_address;
    const canUserMergeValidation = validateIfUserCanMerge(user);

    return (
      <ButtonList>
        <Link to={`/contacts/user/${user.id}`}>
          <ButtonList.Item icon="id-card">View contact</ButtonList.Item>
        </Link>

        <ButtonList.Item
          icon="pen"
          onClick={() => {
            showEditUserModal(user);
          }}
        >
          Edit contact
        </ButtonList.Item>

        {hasMergeAccess && (
          <Tooltip
            content={canUserMergeValidation.reason}
            disabled={canUserMergeValidation.valid}
            side="left"
          >
            <ButtonList.Item
              icon="combine"
              onClick={() => {
                showMergeContactsModal(user);
              }}
              isDisabled={!canUserMergeValidation.valid}
            >
              Merge contact
            </ButtonList.Item>
          </Tooltip>
        )}

        {hasCustomAttributeAccess && (
          <ButtonList.Item
            icon="input"
            onClick={() => {
              showAddContactAttributesModal(user);
            }}
          >
            Edit contact attributes
          </ButtonList.Item>
        )}

        {hasBanAccess && shouldShowBanItem(initial_channel) && !!bans.size && (
          <>
            <ButtonList.Divider />
            {Array.from(bans.keys()).map((channel) => {
              const ban = bans.get(channel);
              return ban ? (
                <UnbanUserButtonListItem
                  key={channel}
                  user={user}
                  channel={channel}
                  onUnban={getActiveUserBansDelayed}
                />
              ) : (
                <BanUserButtonListItem
                  key={channel}
                  user={user}
                  channel={channel}
                  csid={csid}
                  ipAddress={ipAddress}
                  onBan={getActiveUserBansDelayed}
                />
              );
            })}
          </>
        )}

        {flags.agnosticCsatSettings && user.type === 'contact' && (
          <ToggleUserCsatExclusionButtonListItem user={user} />
        )}
      </ButtonList>
    );
  };

  /** Gets the content element for when the component is loading. */
  const getLoadingElement = () => {
    return (
      <CollapseCard.Content>
        <CollapseCard.Status>Loading...</CollapseCard.Status>
      </CollapseCard.Content>
    );
  };

  /** Gets the content element for when there is no contact information. */
  const getNoUserContactElement = () => {
    return (
      <CollapseCard.Content>
        <CollapseCard.Status>No contact info to show</CollapseCard.Status>
      </CollapseCard.Content>
    );
  };

  /** Gets the content element for when there is user contact information. */
  const getUserContactElement = () => {
    const { user, conversation } = props;
    const emailTooltip = hasReplyAccess ? 'Compose email' : '';
    const phoneTooltip = hasReplyAccess ? 'Call' : '';

    return (
      <CollapseCard.Content>
        <FlexBox flexDirection="column" gap="200" padding="0 0 300 0">
          {user?.email && (
            <ContactCardLine
              icon="mail"
              label="Email"
              content={
                hasReplyAccess ? (
                  <a
                    href={`mailto:${user.email}`}
                    onClick={(e) => handleEmailOnClick(e)}
                  >
                    {user.email}
                  </a>
                ) : (
                  user.email
                )
              }
              contentTooltip={emailTooltip}
              actions={
                <>
                  {user?.additional_emails?.length ? (
                    <Badge variant="primary" size="small">
                      Primary
                    </Badge>
                  ) : null}

                  {getCopyToClipboardElement(user.email)}
                </>
              }
            />
          )}

          {user?.additional_emails?.map((additionalEmail) => (
            <ContactCardLine
              key={`additional-email-${additionalEmail}`}
              icon="mail"
              label="Email"
              content={
                hasReplyAccess ? (
                  <a
                    href={`mailto:${additionalEmail}`}
                    onClick={(e) => handleEmailOnClick(e, additionalEmail)}
                  >
                    {additionalEmail}
                  </a>
                ) : (
                  additionalEmail
                )
              }
              contentTooltip={emailTooltip}
              actions={getCopyToClipboardElement(additionalEmail)}
            />
          ))}

          {user?.phone_number && (
            <ContactCardLine
              icon="phone"
              label="Phone"
              content={
                hasReplyAccess ? (
                  <a
                    href={`tel:${user.phone_number}`}
                    onClick={(e) => handleMakeCall(e)}
                  >
                    {getPhoneNumberFormat(user.phone_number)}
                  </a>
                ) : (
                  getPhoneNumberFormat(user.phone_number)
                )
              }
              contentTooltip={phoneTooltip}
              actions={
                <>
                  {user?.additional_phone_numbers?.length ? (
                    <Badge variant="primary" size="small">
                      Primary
                    </Badge>
                  ) : null}

                  {getCopyToClipboardElement(user.phone_number)}
                </>
              }
            />
          )}

          {user?.additional_phone_numbers?.map((additionalPhoneNumber) => (
            <ContactCardLine
              key={`additional-phone-number-${additionalPhoneNumber}`}
              icon="phone"
              label="Phone"
              content={
                hasReplyAccess ? (
                  <a
                    href={`tel:${additionalPhoneNumber}`}
                    onClick={(e) => handleMakeCall(e, additionalPhoneNumber)}
                  >
                    {getPhoneNumberFormat(additionalPhoneNumber)}
                  </a>
                ) : (
                  getPhoneNumberFormat(additionalPhoneNumber)
                )
              }
              contentTooltip={phoneTooltip}
              actions={getCopyToClipboardElement(additionalPhoneNumber)}
            />
          ))}

          {conversation.initial_channel === 'trustpilotreviews' &&
            conversation.attributes?.end_user_contact_point_value && (
              <ContactCardLine
                icon="trustpilot-outlined"
                label="Trustpilot ID"
                content={conversation.attributes.end_user_contact_point_value}
                actions={getCopyToClipboardElement(
                  conversation.attributes.end_user_contact_point_value,
                )}
              />
            )}

          {user?.external_id && (
            <ContactCardLine
              icon="id-card"
              label="External ID"
              content={user.external_id}
              actions={getCopyToClipboardElement(user.external_id)}
            />
          )}

          {customFields.length > 0 && (
            <>
              <FlexBox justifyContent="space-between" alignItems="center">
                <Text.Paragraph variant="3" subdued>
                  Contact attributes
                </Text.Paragraph>

                {hasCustomAttributeAccess && (
                  <IconButton
                    variant="tertiary"
                    icon="pen"
                    onClick={() => {
                      if (props.user) {
                        props.showAddContactAttributesModal(props.user);
                      }
                    }}
                  />
                )}
              </FlexBox>

              {customFields.map((customField) => (
                <ContactCardLine
                  key={customField.attribute.id}
                  icon="input"
                  label={customField.attribute.label}
                  content={
                    <CustomFieldChip
                      key={customField.attribute.id}
                      customField={customField}
                    />
                  }
                />
              ))}
            </>
          )}
        </FlexBox>
      </CollapseCard.Content>
    );
  };

  const getCopyToClipboardElement = (textToCopy: string) => (
    <Tooltip content="Copy">
      <CopyToClipboardButton
        textToCopy={textToCopy}
        tooltip={false}
        clickedProps={{ icon: 'done' }}
        buttonProps={{ variant: 'tertiary', icon: 'copy' }}
      />
    </Tooltip>
  );

  const { user, isMinimized, asyncStatusConversation } = props;
  if (!user) {
    return null;
  }

  const banBadgeLabel = Array.from(bans.keys())
    .filter((channel) => !!bans.get(channel))
    .map((channel) => channelAttributes[channel]?.name)
    .join(', ');

  return isMinimized ? (
    <div className="icon-holder contact">{getAvatarElement()}</div>
  ) : (
    <CollapseCard isOpen={props.isOpen} onToggle={props.onToggle}>
      <CollapseCard.Header
        avatar={getAvatarElement()}
        dropdown={getMenuElement(user)}
        onDropdownToggle={(isOpen) => {
          if (isOpen) {
            ampli.contactsCardActionsMenuOpened();
          }
        }}
      >
        <span style={{ whiteSpace: 'normal' }}>
          {(user.name || user.email || user.phone_number) ?? ''}
        </span>
        {!!banBadgeLabel && (
          <span>
            <Badge variant="error" size="small">
              Banned on {banBadgeLabel}
            </Badge>
          </span>
        )}
      </CollapseCard.Header>
      {asyncStatusConversation === 'loading' && getLoadingElement()}
      {asyncStatusConversation !== 'loading' &&
      !user.phone_number &&
      !user.email
        ? getNoUserContactElement()
        : getUserContactElement()}
    </CollapseCard>
  );
};

const mapStateToProps = (state: RootReducerStateT) => {
  return {
    user: state.conversation.metaConversation.requester,
    conversation: state.conversation.metaConversation.conversation,
    asyncStatusConversation: state.conversation.asyncStatus.getConversation,
    telephonyFromNumber: state.telephony.selectedFromNumber,
  };
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    showEditUserModal: (user: Partial<User>) =>
      dispatch(userModalActions.show({ user })),
    showMergeContactsModal: (user: User) =>
      dispatch(
        appActions.showModal({
          modal: {
            type: 'app:mergeContacts',
            arg: { user },
          },
        }),
      ),
    showAddContactAttributesModal: (user: User) =>
      dispatch(
        appActions.showModal({
          modal: {
            type: 'contact:addContactAttributes',
            arg: { user },
          },
        }),
      ),
    showComposeEmailModal: (payload: ComposeEmailPayload) =>
      dispatch(composeEmailActions.composeEmailOpenModal(payload)),
    telephonyMakeCall: bindActionToPromise<TwilloMakeCallPayload, void, void>(
      dispatch,
      twilioActions.twilioMakeCall,
    ),
  };
};

// TODO: Move to actions when it has been converted to ts.
interface TwilloMakeCallPayload {
  /** The user id. */
  readonly userId: string;
  /** The number to call to. */
  readonly toNumber: string;
  /** The number to call from. */
  readonly fromNumber: string;
}

// TODO: Move to actions when it has been converted to ts.
interface ComposeEmailPayload {
  /** The initial values. */
  readonly initialValues: {
    /** The user for who to compose the email to. */
    readonly toUser: { user: User }[];
  };
}

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;

export default connect(mapStateToProps, mapDispatchToProps)(ContactCard);
